import type {
  BankReconciliationStatus,
  FinanceAlert,
  FinanceCategoryKind,
  FinancialTransactionKind,
  FinancialTransactionStatus,
  MoneyValue,
  PayerType,
  ReconciliationStatus,
} from '@mfos/shared';
import type { BadgeTone } from '../../components/Badge';
import { getFormatLocale } from '../../lib/format';

/**
 * Formats an API decimal string ("-1234.5000") without binary floating point: digits grouped,
 * at least two decimals, insignificant trailing zeros beyond that removed ("-1,234.50").
 */
export function formatAmount(amount: string): string {
  const match = /^([-+]?)(\d+)(?:\.(\d+))?$/.exec(amount.trim());
  if (match === null) return amount;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmed = fraction.replace(/0+$/, '').padEnd(2, '0');
  const isZero = /^0*$/.test(whole) && /^0*$/.test(fraction);
  return `${isZero ? '' : sign}${grouped}.${trimmed}`;
}

export function formatMoney(money: MoneyValue): string {
  return `${formatAmount(money.amount)} ${money.currency}`;
}

export function isNegativeAmount(amount: string): boolean {
  const value = amount.trim();
  return value.startsWith('-') && /[1-9]/.test(value);
}

const HY_MONTHS = [
  'հունվար',
  'փետրվար',
  'մարտ',
  'ապրիլ',
  'մայիս',
  'հունիս',
  'հուլիս',
  '\u0585\u0563\u0578\u057d\u057f\u0578\u057d',
  'սեպտեմբեր',
  'հոկտեմբեր',
  'նոյեմբեր',
  'դեկտեմբեր',
];

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "2026-10" → "Oct 2026". */
export function formatMonth(month: string): string {
  const [year, number] = month.split('-');
  const names = getFormatLocale() === 'HY' ? HY_MONTHS : MONTH_NAMES;
  const name = names[Number(number) - 1];
  return name === undefined || year === undefined ? month : `${name} ${year}`;
}

/** Calendar month `YYYY-MM` shifted by `offset` months. */
export function shiftMonth(month: string, offset: number): string {
  const [year = '1970', number = '1'] = month.split('-');
  const index = Number(year) * 12 + Number(number) - 1 + offset;
  const shiftedYear = Math.floor(index / 12);
  return `${String(shiftedYear)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export const KIND_LABELS: Readonly<Record<FinancialTransactionKind, string>> = {
  INCOME: 'Income',
  EXPENSE: 'Expense',
};

export const CATEGORY_KIND_LABELS: Readonly<Record<FinanceCategoryKind, string>> = {
  INCOME: 'Income',
  EXPENSE: 'Expense',
  TAX: 'Tax',
  TRANSFER: 'Transfer',
  EQUITY: 'Equity',
  OTHER: 'Other',
};

export const PAYER_LABELS: Readonly<Record<PayerType, string>> = {
  COMPANY: 'Company account',
  FOUNDER: 'Paid by a founder',
};

export const TRANSACTION_STATUS: Readonly<
  Record<FinancialTransactionStatus, { label: string; tone: BadgeTone }>
> = {
  DRAFT: { label: 'Draft — not in figures', tone: 'info' },
  RECORDED: { label: 'Recorded', tone: 'ok' },
  VOIDED: { label: 'Void (reversed)', tone: 'neutral' },
};

export const RECONCILIATION_STATUS: Readonly<
  Record<ReconciliationStatus, { label: string; tone: BadgeTone }>
> = {
  UNRECONCILED: { label: 'Not reconciled', tone: 'warn' },
  MATCHED: { label: 'Matched', tone: 'info' },
  RECONCILED: { label: 'Reconciled', tone: 'ok' },
  DISPUTED: { label: 'Disputed', tone: 'down' },
  EXCLUDED: { label: 'Excluded', tone: 'neutral' },
};

export const MOVEMENT_STATUS: Readonly<
  Record<BankReconciliationStatus, { label: string; tone: BadgeTone }>
> = {
  UNRECONCILED: { label: 'Needs review', tone: 'warn' },
  MATCHED: { label: 'Matched', tone: 'info' },
  RECONCILED: { label: 'Reconciled', tone: 'ok' },
  SUSPECTED_DUPLICATE: { label: 'Possible duplicate', tone: 'down' },
  EXCLUDED: { label: 'Excluded', tone: 'neutral' },
};

export const MOVEMENT_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'UNRECONCILED', label: 'Needs review' },
  { value: 'SUSPECTED_DUPLICATE', label: 'Possible duplicates' },
  { value: 'RECONCILED', label: 'Reconciled' },
  { value: 'EXCLUDED', label: 'Excluded' },
];

export const ALERT_TONES: Readonly<Record<FinanceAlert['severity'], BadgeTone>> = {
  WARNING: 'warn',
  INFO: 'info',
};

export const RUNWAY_STATUS_LABELS = {
  BURNING: 'Burning cash',
  NOT_BURNING: 'Not burning cash',
  INSUFFICIENT_DATA: 'Not enough data',
} as const;
